import React, { useRef, useEffect, useState } from "react";
import MonacoEditor from "@monaco-editor/react";
import { DebugStatus, DrawingTool } from "../../types";
import { useCookiePreferences } from "../../hooks/useCookiePreferences";
import useDeviceType from "../../hooks/useDeviceType";
import DrawingOverlay from "./DrawingOverlay";
import { shareCode, validateCodeForSharing } from "../../services";
import { Button } from "../atoms/button";
import { Eyebrow } from "../atoms/eyebrow";
import { DisplayHeading } from "../atoms/display-heading";
import { FontControls } from "../molecules/font-controls";

interface CodeEditorProps {
	code: string;
	onChange: (code: string) => void;
	isReady: boolean;
	isDebugging: boolean;
	currentLine?: number;
	onDebug: () => void;
	onStep: () => void;
	onStepBackward: () => void;
	onStepToFirst: () => void;
	onStepToLast: () => void;
	onStepToPosition: (position: number) => void;
	onRun: () => void;
	onReset: () => void;
	canStep: boolean;
	canStepBack: boolean;
	status: DebugStatus;
	statusText: string;
	currentStep: number;
	totalSteps: number;
	title?: string;
	fontSize?: number;
	onIncreaseFontSize?: () => void;
	onDecreaseFontSize?: () => void;
	onResetFontSize?: () => void;
	isExpanded?: boolean;
	onToggleExpand?: () => void;
	enableSharing?: boolean;
}

const CodeEditor: React.FC<CodeEditorProps> = ({
	code,
	onChange,
	isReady,
	isDebugging,
	currentLine,
	onDebug,
	onStep,
	onStepBackward,
	onStepToFirst,
	onStepToLast,
	onStepToPosition,
	onRun,
	onReset,
	canStep,
	canStepBack,
	status,
	statusText,
	currentStep,
	totalSteps,
	title = "Code Editor",
	fontSize = 12,
	onIncreaseFontSize,
	onDecreaseFontSize,
	onResetFontSize,
	isExpanded = false,
	onToggleExpand,
	enableSharing = false,
}) => {
	const { isDarkMode } = useCookiePreferences();
	console.log("CodeEditor render: isDarkMode =", isDarkMode);
	const { isMobile } = useDeviceType();
	const editorRef = useRef<any>(null);
	const monacoRef = useRef<any>(null);
	const decorationsRef = useRef<string[]>([]);
	const layoutTimeoutRef = useRef<NodeJS.Timeout | null>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	// Drawing state
	const [isDrawingMode, setIsDrawingMode] = useState(false);
	const [currentTool, setCurrentTool] = useState<DrawingTool>("pen");
	const [strokeColor, setStrokeColor] = useState("#ef4444");
	const [lineWidth, setLineWidth] = useState(2);

	// Share functionality state
	const [showShareSuccess, setShowShareSuccess] = useState(false);
	const [shareWarning, setShareWarning] = useState<string | null>(null);

	// Disable drawing mode on mobile devices
	useEffect(() => {
		if (isMobile && isDrawingMode) {
			setIsDrawingMode(false);
		}
	}, [isMobile, isDrawingMode]);

	// Handle line highlighting with green arrow for current line
	useEffect(() => {
		if (editorRef.current && isDebugging && currentLine) {
			const editor = editorRef.current;

			// Clear previous decorations
			decorationsRef.current = editor.deltaDecorations(
				decorationsRef.current,
				[],
			);

			// Add current line decoration (green arrow)
			const decorations = [
				{
					range: {
						startLineNumber: currentLine,
						startColumn: 1,
						endLineNumber: currentLine,
						endColumn: 1,
					},
					options: {
						isWholeLine: true,
						className: "debug-current-line-highlight",
						glyphMarginClassName: "debug-current-line-glyph",
					},
				},
			];

			decorationsRef.current = editor.deltaDecorations([], decorations);
			editor.revealLineInCenter(currentLine);
		}
	}, [currentLine, isDebugging]);

	// Clear decorations when not debugging
	useEffect(() => {
		if (!isDebugging && editorRef.current) {
			decorationsRef.current = editorRef.current.deltaDecorations(
				decorationsRef.current,
				[],
			);
		}
	}, [isDebugging]);

	// State to force Monaco editor re-mount when theme changes
	const [editorKey, setEditorKey] = useState(0);

	// Update Monaco editor theme when dark mode changes
	useEffect(() => {
		console.log("CodeEditor: isDarkMode changed to:", isDarkMode);

		// Method 1: Try to update theme directly if editor is already mounted
		if (editorRef.current && monacoRef.current) {
			const newTheme = isDarkMode ? "vs-dark" : "vs-light";
			console.log("Attempting to set theme to:", newTheme);

			try {
				// Try the global Monaco instance first
				if ((window as any).monaco?.editor?.setTheme) {
					console.log("Setting theme via global Monaco");
					(window as any).monaco.editor.setTheme(newTheme);
				}

				// Also try updating editor options
				if (editorRef.current.updateOptions) {
					console.log("Updating editor options");
					editorRef.current.updateOptions({ theme: newTheme });
				}

				// Force layout update
				setTimeout(() => {
					if (editorRef.current?.layout) {
						editorRef.current.layout();
					}
				}, 100);
			} catch (error) {
				console.error("Failed to update theme, forcing re-mount:", error);
				// If direct theme update fails, force re-mount
				setEditorKey((prev) => prev + 1);
			}
		} else {
			console.log("Editor not ready, will set theme on mount");
		}
	}, [isDarkMode]);

	// Force editor re-layout when container size changes
	useEffect(() => {
		if (!containerRef.current) return;

		let lastWidth = 0;
		let lastHeight = 0;
		const resizeState = { isResizing: false };

		const finishResize = () => {
			if (editorRef.current) {
				try {
					editorRef.current.layout();
				} catch (error) {
					// Ignore layout errors silently
				}
			}
			resizeState.isResizing = false;
		};

		const resizeObserver = new ResizeObserver((entries) => {
			// Prevent cascading resize events
			if (resizeState.isResizing) return;

			// Use requestAnimationFrame to avoid blocking the main thread
			requestAnimationFrame(() => {
				try {
					for (const entry of entries) {
						const { width, height } = entry.contentRect;

						// Only trigger layout if size actually changed significantly (increased threshold)
						if (
							Math.abs(width - lastWidth) > 10 ||
							Math.abs(height - lastHeight) > 10
						) {
							lastWidth = width;
							lastHeight = height;

							resizeState.isResizing = true;

							// Clear any pending layout calls
							if (layoutTimeoutRef.current) {
								clearTimeout(layoutTimeoutRef.current);
							}

							// Use longer delay to prevent rapid successive calls
							layoutTimeoutRef.current = setTimeout(finishResize, 100);
						}
					}
				} catch (error) {
					resizeState.isResizing = false;
					// Silently ignore ResizeObserver errors to prevent console spam
				}
			});
		});

		resizeObserver.observe(containerRef.current);

		return () => {
			resizeObserver.disconnect();
			if (layoutTimeoutRef.current) {
				clearTimeout(layoutTimeoutRef.current);
			}
			resizeState.isResizing = false;
		};
	}, []);

	// Global keyboard shortcuts handler
	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			const isEditor =
				event.target && (event.target as HTMLElement).closest(".monaco-editor");
			if (!isEditor) return; // Only handle shortcuts when editor is focused

			const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
			const cmdKey = isMac ? event.metaKey : event.ctrlKey;

			// Cmd/Ctrl + Enter: Run code
			if (cmdKey && event.key === "Enter" && !event.shiftKey) {
				event.preventDefault();
				event.stopPropagation();
				onRun();
				return;
			}

			// Cmd/Ctrl + R: Run code (when not debugging)
			if (cmdKey && event.key === "r" && !event.shiftKey && !isDebugging) {
				event.preventDefault();
				event.stopPropagation();
				onRun();
				return;
			}

			// Cmd/Ctrl + D: Start Learn Mode or Step Forward
			if (cmdKey && event.key === "d" && !event.shiftKey) {
				event.preventDefault();
				event.stopPropagation();
				if (isDebugging) {
					onStep();
				} else {
					onDebug();
				}
				return;
			}

			// F9: Learn Mode
			if (event.key === "F9") {
				event.preventDefault();
				event.stopPropagation();
				if (isDebugging) {
					onStep();
				} else {
					onDebug();
				}
				return;
			}

			// F10: Step Forward
			if (event.key === "F10" && !event.shiftKey && isDebugging && canStep) {
				event.preventDefault();
				event.stopPropagation();
				onStep();
				return;
			}

			// Shift + F10: Step Backward
			if (event.key === "F10" && event.shiftKey && isDebugging && canStepBack) {
				event.preventDefault();
				event.stopPropagation();
				onStepBackward();
				return;
			}

			// Escape: Exit Learn Mode
			if (event.key === "Escape" && isDebugging) {
				event.preventDefault();
				event.stopPropagation();
				onReset();
				return;
			}
		};

		document.addEventListener("keydown", handleKeyDown, true);
		return () => {
			document.removeEventListener("keydown", handleKeyDown, true);
		};
	}, [
		isDebugging,
		canStep,
		canStepBack,
		onRun,
		onDebug,
		onStep,
		onStepBackward,
		onReset,
	]);

	// Cleanup function
	useEffect(() => {
		return () => {
			if (layoutTimeoutRef.current) {
				clearTimeout(layoutTimeoutRef.current);
			}
		};
	}, []);

	// Drawing callbacks
	const handleToggleDrawingMode = () => {
		setIsDrawingMode(!isDrawingMode);
	};

	const handleToolChange = (tool: DrawingTool) => {
		setCurrentTool(tool);
	};

	const handleColorChange = (color: string) => {
		setStrokeColor(color);
	};

	const handleLineWidthChange = (width: number) => {
		setLineWidth(width);
	};

	const handleClearDrawings = () => {
		// Additional cleanup if needed
	};

	// Share functionality
	const handleShareCode = async () => {
		try {
			// Validate code before sharing
			const validation = validateCodeForSharing(code);

			if (!validation.valid) {
				setShareWarning(validation.warning || "Code cannot be shared");
				setTimeout(() => setShareWarning(null), 5000);
				return;
			}

			// Clear any previous warnings
			setShareWarning(null);

			const result = await shareCode(code);

			if (result.success) {
				setShowShareSuccess(true);
				setTimeout(() => setShowShareSuccess(false), 2000);
			} else {
				console.error("Failed to share code:", result.error);
				// Still show success for the URL generation part, even if clipboard failed
				setShowShareSuccess(true);
				setTimeout(() => setShowShareSuccess(false), 2000);
			}
		} catch (error) {
			console.error("Unexpected error during code sharing:", error);
		}
	};

	const handleEditorDidMount = (editor: any, monaco: any) => {
		editorRef.current = editor;
		monacoRef.current = monaco;

		// Debounced layout function to prevent rapid resize loops
		const debouncedLayout = () => {
			if (layoutTimeoutRef.current) clearTimeout(layoutTimeoutRef.current);
			layoutTimeoutRef.current = setTimeout(() => {
				if (editor && editorRef.current === editor) {
					try {
						editor.layout();
					} catch (error) {
						// Ignore layout errors silently
					}
				}
			}, 100);
		};

		// Initial layout
		debouncedLayout();

		// Note: Keyboard shortcuts are now handled by global event listener
		// for better compatibility and reliability
	};

	return (
		<div
			ref={containerRef}
			className="flex flex-col h-full overflow-hidden animate-fade-in"
		>
			{/* Header */}
			<div className="panel-header flex-shrink-0 !py-2.5">
				<div className="flex items-center justify-between min-w-0 gap-3">
					<div className="flex items-baseline gap-2 min-w-0 flex-shrink">
						<Eyebrow className="!text-[9px]">Editor</Eyebrow>
						<DisplayHeading size="sm" className="!text-[15px] truncate">
							{title}
						</DisplayHeading>
						{!isMobile && (
							<>
								<span className="text-gray-300 dark:text-gray-700 text-[11px]">
									·
								</span>
								<span className="mono text-[10px] text-gray-500 dark:text-gray-500 tracking-tight">
									main.py
								</span>
							</>
						)}
					</div>
					<div className="flex items-center gap-1 min-w-0 flex-wrap justify-end">
						{onToggleExpand && (
							<Button
								variant="ghost"
								size="xs"
								onClick={onToggleExpand}
								title={isExpanded ? "Shrink" : "Expand"}
							>
								<svg
									className="w-3 h-3"
									fill="none"
									stroke="currentColor"
									strokeWidth="1.7"
									viewBox="0 0 24 24"
								>
									{isExpanded ? (
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M15 15v4.5M15 15h4.5M15 15l5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M9 15H4.5M9 15v4.5M9 15l-5.25 5.25"
										/>
									) : (
										<path
											strokeLinecap="round"
											strokeLinejoin="round"
											d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
										/>
									)}
								</svg>
								<span className="hidden sm:inline">
									{isExpanded ? "Shrink" : "Expand"}
								</span>
							</Button>
						)}

						{!isMobile && (
							<Button
								variant={isDrawingMode ? "warningOutline" : "ghost"}
								size="xs"
								onClick={handleToggleDrawingMode}
								disabled={isDebugging}
								className="hidden md:inline-flex"
								title={isDrawingMode ? "Stop drawing" : "Draw annotations"}
							>
								<svg
									className="w-3 h-3"
									fill="none"
									stroke="currentColor"
									strokeWidth="1.7"
									viewBox="0 0 24 24"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
									/>
								</svg>
								<span className="hidden lg:inline">
									{isDrawingMode ? "Exit" : "Draw"}
								</span>
							</Button>
						)}

						<FontControls
							onIncrease={onIncreaseFontSize}
							onDecrease={onDecreaseFontSize}
							onReset={onResetFontSize}
							className="ml-1"
						/>

						{enableSharing && (
							<Button
								variant={showShareSuccess ? "success" : "secondary"}
								size="xs"
								onClick={handleShareCode}
								className={showShareSuccess ? "animate-scale-in" : undefined}
								title="Copy shareable link"
							>
								{showShareSuccess ? (
									<>
										<svg
											className="w-3 h-3"
											fill="none"
											stroke="currentColor"
											strokeWidth="2.2"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M5 13l4 4L19 7"
											/>
										</svg>
										<span className="hidden sm:inline">Copied</span>
									</>
								) : (
									<>
										<svg
											className="w-3 h-3"
											fill="none"
											stroke="currentColor"
											strokeWidth="1.7"
											viewBox="0 0 24 24"
										>
											<path
												strokeLinecap="round"
												strokeLinejoin="round"
												d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z"
											/>
										</svg>
										<span className="hidden sm:inline">Share</span>
									</>
								)}
							</Button>
						)}
					</div>
				</div>

				{shareWarning && (
					<div className="mt-2 px-2.5 py-1.5 bg-amber-50 dark:bg-amber-500/[0.08] border border-amber-200 dark:border-amber-500/20 rounded-md text-[11px] text-amber-800 dark:text-amber-300 flex items-center gap-2">
						<svg
							className="w-3 h-3 flex-shrink-0"
							fill="none"
							stroke="currentColor"
							strokeWidth="1.8"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.99-.833-2.464 0L4.732 16.5c-.77.833.192 2.5 1.732 2.5z"
							/>
						</svg>
						<span>{shareWarning}</span>
					</div>
				)}
			</div>

			{/* Monaco Editor */}
			<div className="flex-1 relative overflow-hidden cursor-text">
				<MonacoEditor
					key={`editor-${editorKey}-${isDarkMode ? "dark" : "light"}`}
					height="100%"
					language="python"
					theme={isDarkMode ? "vs-dark" : "vs-light"}
					value={code}
					onChange={(value) => onChange(value || "")}
					onMount={handleEditorDidMount}
					loading={
						<div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
							Loading editor...
						</div>
					}
					options={{
						fontSize: fontSize,
						lineNumbers: "on",
						roundedSelection: false,
						scrollBeyondLastLine: false,
						automaticLayout: true,
						minimap: { enabled: false },
						wordWrap: "off",
						tabSize: 4,
						insertSpaces: true,
						bracketPairColorization: { enabled: true },
						smoothScrolling: true,
						cursorBlinking: "smooth",
						renderLineHighlight: "gutter",
						fontFamily:
							"'Source Code Pro', 'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
						fontLigatures: true,
						padding: { top: 12, bottom: 12 },
						contextmenu: true,
						mouseWheelZoom: true,
						selectOnLineNumbers: true,
						glyphMargin: true,
						folding: false,
						renderFinalNewline: "on",
						readOnly: isDebugging || isDrawingMode,
						lineNumbersMinChars: 2,
						scrollbar: {
							horizontal: "auto",
							vertical: "auto",
						},
					}}
				/>

				{/* Drawing Overlay */}
				<DrawingOverlay
					isDrawingMode={isDrawingMode}
					currentTool={currentTool}
					strokeColor={strokeColor}
					lineWidth={lineWidth}
					onToggleDrawingMode={handleToggleDrawingMode}
					onToolChange={handleToolChange}
					onColorChange={handleColorChange}
					onLineWidthChange={handleLineWidthChange}
					onClearDrawings={handleClearDrawings}
				/>
			</div>

			{/* Inspect Controls */}
			{isDebugging && totalSteps > 0 ? (
				<div className="flex-shrink-0 px-3.5 py-2.5 border-t border-gray-200/80 dark:border-white/[0.06] bg-white/80 dark:bg-[#0c0c0e]/80 backdrop-blur-md space-y-2.5">
					<div className="flex items-center justify-between">
						<div className="flex items-baseline gap-2">
							<Eyebrow className="!text-[9px]">Step</Eyebrow>
							<DisplayHeading size="sm" className="tabular">
								{currentStep + 1}
							</DisplayHeading>
							<span className="text-gray-300 dark:text-gray-700 text-xs">/</span>
							<span className="text-[11px] tabular text-gray-500 dark:text-gray-500">
								{totalSteps}
							</span>
						</div>
						<Eyebrow className="!text-[9px]">
							{currentStep === 0
								? "Start"
								: currentStep >= totalSteps - 1
									? "Final"
									: "Running"}
						</Eyebrow>
					</div>

					<input
						type="range"
						min="0"
						max={totalSteps - 1}
						value={currentStep}
						onChange={(e) => onStepToPosition(parseInt(e.target.value))}
						className="slider w-full cursor-pointer"
						title="Drag to jump to any step"
					/>

					<div className="flex items-center justify-center gap-1 flex-wrap">
						<Button
							variant="secondary"
							size="xs"
							onClick={onStepToFirst}
							disabled={currentStep === 0}
							title="Jump to beginning"
						>
							<svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
								<path d="M5 4l-1 0 0 16 1 0 0-16zM7 12l11-7v14L7 12z" />
							</svg>
							Start
						</Button>
						<Button
							variant="secondary"
							size="xs"
							onClick={onStepBackward}
							disabled={!canStepBack}
							title="Back one step (Shift+F10)"
						>
							<svg
								className="w-3 h-3"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M15.75 19.5L8.25 12l7.5-7.5"
								/>
							</svg>
							Back
						</Button>
						<Button
							variant="primary"
							size="xs"
							onClick={onStep}
							disabled={!canStep}
							title="Run next line (F10)"
						>
							Next
							<svg
								className="w-3 h-3"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M8.25 4.5l7.5 7.5-7.5 7.5"
								/>
							</svg>
						</Button>
						<Button
							variant="secondary"
							size="xs"
							onClick={onStepToLast}
							disabled={currentStep >= totalSteps - 1}
							title="Jump to final step"
						>
							End
							<svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
								<path d="M5 4a1 1 0 011.6-.8l5.334 4a1 1 0 010 1.6l-5.334 4A1 1 0 015 12V4zM13 4a1 1 0 011.6-.8l5.334 4a1 1 0 010 1.6l-5.334 4A1 1 0 0113 12V4z" />
							</svg>
						</Button>
					</div>
				</div>
			) : null}
		</div>
	);
};

export default CodeEditor;
